/** Карточки «Аналитики ОПТ» на общем каркасе `components/EntityPanelFrame` (как в «Аналитике МП»:
 *  Esc, фокус, «Назад» к предыдущей карточке).
 *
 *  Сущность — покупатель (`b:`), товар (`p:`), заказ (`o:`), узел дерева (`t:`): заголовок, KPI
 *  отгрузок периода с Δ (сетка МП), портфель заказов (к отгрузке — в периоде, просрочено — все на
 *  дату снимка; без Δ, плановая маржа) — из `/node` (те же цифры, что строка дерева), блок «Строки»
 *  — `/lines` с тем же фильтром (отгрузки и к отгрузке периода + все просроченные). Из строк — переход в карточку покупателя / товара /
 *  заказа (новая запись истории, «Назад» — обратно).
 *
 *  Списки — месяц × вид (`m:`), портфель вида (`op:`), «без себестоимости» (`u:`) / «без ставки
 *  склада» (`n:`; сначала список товаров): итоги по видам по ВСЕМ подходящим строкам и строки;
 *  обрезка списка — громко. */
import { useQueryClient, type InfiniteData, type QueryClient } from "@tanstack/react-query";
import type React from "react";

import type { LinesQuery, Line, PerKind, TreeDim, TreeNode, WholesaleTree } from "@/api/wholesale";
import { EntityPanelFrame } from "@/components/EntityPanelFrame";
import { cn } from "@/lib/utils";
import { Block } from "../ozon-analytics/cardBlocks";
import { fmtDate, monthLabel } from "../ozon-analytics/format";
import { tdNum, thClass } from "../ozon-analytics/ui";
import {
  cardQuery,
  COMMISSIONER_HINT,
  docLabel,
  encodeCard,
  entitySteps,
  isCommissioner,
  KIND_LABEL,
  marginCell,
  periodLabel,
  productTitle,
  qty,
  revenueCell,
  rubOrDash,
  stepsText,
  TREE_DIMS,
  ZERO_AMOUNT_LABEL,
  type Card,
  type MonthRange,
  type NodeStep,
} from "./calc";
import { AMBER, ErrorBox, MoneyTd, Portfolio, ShippedKpis } from "./parts";
import { ProductsTable } from "./Tables";
import { useWholesaleLines, useWholesaleNode, useWholesaleProducts } from "./queries";

const STATUS_LABEL: Record<string, string> = {
  no_price: "нет цены",
  no_warehouse_rate: "нет ставки склада",
  zero_price: "сумма заказа 0",
  no_order_line: "нет строки заказа",
  unknown_unit: "единица не распознана",
};

const ENTITY_LABEL: Record<NodeStep["dim"], string> = {
  buyer: "Карточка покупателя",
  product: "Карточка товара",
  characteristic: "Карточка характеристики",
  month: "Карточка месяца",
  order: "Карточка заказа",
};

/** Подпись вида карточки в шапке панели. */
function cardLabel(c: Card): string {
  switch (c.type) {
    case "buyer":
      return ENTITY_LABEL.buyer;
    case "product":
      return ENTITY_LABEL.product;
    case "order":
      return ENTITY_LABEL.order;
    case "node":
      return ENTITY_LABEL[c.steps[c.steps.length - 1]!.dim];
    case "month":
      return "Строки месяца";
    case "portfolio":
      return "Портфель заказов";
    case "unpriced":
      return "Без себестоимости";
    case "norate":
      return "Без ставки склада";
  }
}

function listTitle(c: Card, r: MonthRange): string {
  switch (c.type) {
    case "month":
      return `${KIND_LABEL[c.kind]} · ${monthLabel(c.month)}`;
    case "portfolio":
      return c.kind === "planned"
        ? `К отгрузке · ${periodLabel(r)} (дата отгрузки в периоде)`
        : "Просрочено — все просроченные на дату снимка";
    case "unpriced":
      return `Без себестоимости · ${periodLabel(r)} + все просроченные`;
    case "norate":
      return `Без ставки склада · ${periodLabel(r)} + все просроченные`;
    default:
      return "";
  }
}

function Badges({ l }: { l: Line }): React.ReactElement {
  // покупатель без ИНН не помечается (решение владельца 22.10.2026: иностранные — норма)
  const marks: string[] = [];
  if (l.price_status !== "ok") marks.push(STATUS_LABEL[l.price_status] ?? l.price_status);
  // отгрузка за 0 ₽ (v1.4) — не проблема: нейтральная пометка ниже
  if (l.revenue_status !== "ok" && l.revenue_status !== "zero_amount") {
    marks.push(STATUS_LABEL[l.revenue_status] ?? l.revenue_status);
  }
  if (l.month_basis === "base_unresolved") marks.push("исходная накладная не найдена");
  return (
    <>
      {marks.map((m) => (
        <span key={m} className="mr-1 rounded bg-amber-100 px-1 text-[10px] text-amber-800 dark:bg-amber-950 dark:text-amber-300" data-testid="line-badge">
          {m}
        </span>
      ))}
      {l.revenue_status === "zero_amount" && (
        <span
          className="mr-1 rounded bg-muted px-1 text-[10px] text-muted-foreground"
          title="отгрузка за 0 ₽: штуки в отгрузках, выручка 0, маржа — только расход"
          data-testid="line-note"
        >
          {ZERO_AMOUNT_LABEL}
        </span>
      )}
    </>
  );
}

const LINK = "text-left hover:underline focus-visible:underline focus-visible:outline-none";

function Totals({ totals }: { totals: PerKind }): React.ReactElement {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" data-testid="lines-totals">
      {(["shipped", "planned", "overdue"] as const).map((k) => {
        const a = totals[k];
        const m = marginCell(a);
        return (
          <div key={k} className="rounded-md border px-3 py-2 text-xs">
            <div className="text-muted-foreground">{KIND_LABEL[k]}</div>
            {a ? (
              <>
                <div className="text-base font-semibold tabular-nums" title={revenueCell(a).title ?? undefined}>
                  {revenueCell(a).text}
                  {revenueCell(a).partial && <span className="text-amber-600 dark:text-amber-400" data-testid="partial">*</span>}
                </div>
                <div>
                  {qty(a.quantity)} шт · маржа {m.text}
                  {m.partial && <span className="text-amber-600 dark:text-amber-400" title={m.title ?? undefined}>*</span>}
                </div>
              </>
            ) : (
              <div className="text-base">—</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function LinesTable({ lines, onOpen }: { lines: Line[]; onOpen: (c: Card) => void }): React.ReactElement {
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-xs" data-testid="lines-table">
        <thead>
          <tr className="border-b">
            <th className={cn(thClass, "text-left")}>Дата</th>
            <th className={cn(thClass, "text-left")}>Документ</th>
            <th className={cn(thClass, "text-left")}>Покупатель</th>
            <th className={cn(thClass, "text-left")}>Товар</th>
            <th className={thClass}>Шт</th>
            <th className={thClass}>Выручка</th>
            <th className={thClass}>Цена/шт</th>
            <th className={thClass}>Себестоимость</th>
            <th className={thClass}>Склад</th>
            <th className={thClass}>Маржа</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => {
            const newDoc = i === 0 || lines[i - 1]!.document_ref !== l.document_ref;
            return (
              <tr key={l.row_id} className={cn(newDoc && "border-t")} data-testid="line-row" title={l.method}>
                <td className="px-2 py-1 whitespace-nowrap">{newDoc ? fmtDate(l.date) : ""}</td>
                <td className="px-2 py-1">
                  {newDoc && (
                    <div>
                      {docLabel(l.document_type)}
                      {l.order_number && l.order_ref && (
                        <>
                          {" · "}
                          <button
                            type="button"
                            className={LINK}
                            onClick={() => onOpen({ type: "order", ref: l.order_ref! })}
                            data-testid="line-order"
                          >
                            заказ {l.order_number}
                          </button>
                        </>
                      )}
                      {l.month_basis === "base_shipment" && l.base_date && ` · к отгрузке ${fmtDate(l.base_date)}`}
                    </div>
                  )}
                  <div className="text-muted-foreground">
                    {KIND_LABEL[l.kind]} · {monthLabel(l.month)}
                  </div>
                </td>
                <td className="px-2 py-1">
                  <button
                    type="button"
                    className={LINK}
                    onClick={() => onOpen({ type: "buyer", ref: l.counterparty_ref })}
                    data-testid="line-buyer"
                  >
                    {l.buyer_name ?? l.counterparty_ref}
                  </button>
                </td>
                <td className="px-2 py-1">
                  <button
                    type="button"
                    className={LINK}
                    onClick={() =>
                      onOpen({ type: "product", nomenclature: l.nomenclature_ref, characteristic: l.characteristic_ref ?? "" })
                    }
                    data-testid="line-product"
                  >
                    {productTitle(l)}
                  </button>
                  <div className="text-muted-foreground">{l.characteristic_name ?? "без характеристики"}</div>
                  <Badges l={l} />
                </td>
                <td className={tdNum}>{qty(l.quantity)}</td>
                <td className={tdNum}>{rubOrDash(l.revenue_gross)}</td>
                <td className={tdNum}>{rubOrDash(l.purchase_price_gross)}</td>
                <td className={tdNum} data-testid="line-goods" title={l.cogs_goods === null ? "нет себестоимости" : undefined}>
                  {l.cogs_goods === null ? "—" : rubOrDash(-l.cogs_goods)}
                </td>
                <td className={tdNum} data-testid="line-wh" title={l.cogs_wh === null ? "нет ставки склада" : undefined}>
                  {l.cogs_wh === null ? "—" : rubOrDash(-l.cogs_wh)}
                </td>
                <MoneyTd cell={{ text: rubOrDash(l.net_margin), partial: false, title: null }} testId="line-margin" />
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** Блок «Строки»: строки витрины по фильтру карточки (новые сверху), обрезка — громко. */
function LinesBlock({
  query,
  totals,
  onOpen,
}: {
  query: LinesQuery;
  /** итоги по видам над строками — у списков; у сущности итоги — KPI и портфель выше */
  totals: boolean;
  onOpen: (c: Card) => void;
}): React.ReactElement {
  const q = useWholesaleLines(query);
  return (
    <Block title="Строки">
      <div className="space-y-2" data-testid="lines-block">
        {q.isError && <ErrorBox error={q.error} what="строки" />}
        {!q.data && !q.isError && <div className="text-sm text-muted-foreground">Загружаем строки…</div>}
        {q.data && (
          <>
            {totals && <Totals totals={q.data.totals} />}
            {q.data.truncated && (
              <div role="status" className={AMBER} data-testid="lines-truncated">
                {q.data.filters.portfolio ? (
                  <>
                    Показаны {q.data.lines.length} из {q.data.total} строк (сначала отгрузки периода, затем открытые заказы;
                    новые сверху) — итоги по всем {q.data.total}. Просроченные — все, не только за период: если не вошли
                    заказы, откройте карточку уже — заказа или товара.
                  </>
                ) : (
                  <>
                    Показаны {q.data.lines.length} из {q.data.total} строк (новые сверху) — итоги по всем {q.data.total}.
                    Сузьте период, чтобы увидеть остальные.
                  </>
                )}
              </div>
            )}
            {q.data.lines.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="lines-empty">
                Строк нет.
              </p>
            ) : (
              <LinesTable lines={q.data.lines} onOpen={onOpen} />
            )}
          </>
        )}
      </div>
    </Block>
  );
}

/** Список проблемных товаров: «без себестоимости» (только `no_price`) или «без ставки склада»
 *  (только `no_warehouse_rate`) — строки одного списка в другой не попадают. Область — отгрузки и
 *  к отгрузке периода + все просроченные, как у значка покрытия. */
function ProblemProducts({
  range,
  onOpen,
  kind,
}: {
  range: MonthRange;
  onOpen: (c: Card) => void;
  kind: "unpriced" | "no_rate";
}): React.ReactElement {
  const q = useWholesaleProducts(range, kind, true, true);
  const title = kind === "unpriced" ? "Товары без себестоимости" : "Товары без ставки склада";
  if (q.isError) return <ErrorBox error={q.error} what={title.toLowerCase()} />;
  if (!q.data) return <div className="text-sm text-muted-foreground">Загружаем: {title.toLowerCase()}…</div>;
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium">{title}</h3>
      <ProductsTable rows={q.data.products} onOpen={onOpen} testId={kind === "unpriced" ? "unpriced-table" : "norate-table"} />
    </section>
  );
}

/** Заголовок карточки сущности. Подпись — у шага пути (узел дерева), иначе — у узла `/node`, иначе —
 *  из первой строки; ни того ни другого (в периоде и портфеле пусто) — ключ с названием разреза,
 *  а не вечное «…». */
function subjectOf(card: Card, steps: NodeStep[], node: TreeNode | null, first: Line | undefined, loaded: boolean): string {
  const wait = (key: string): string => (loaded ? key : "…");
  switch (card.type) {
    case "buyer":
      return node?.label ?? first?.buyer_name ?? wait(`покупатель ${card.ref}`);
    case "order":
      return node?.label ?? first?.order_number ?? wait(`заказ ${card.ref}`);
    case "product": {
      if (card.characteristic === null) {
        return `${node?.label ?? (first ? productTitle(first) : wait(`товар ${card.nomenclature}`))} · все характеристики`;
      }
      const product = first ? productTitle(first) : wait(`товар ${card.nomenclature}`);
      const ch =
        node?.label ??
        first?.characteristic_name ??
        (card.characteristic === "" ? "без характеристики" : wait(`характеристика ${card.characteristic}`));
      return `${product} · ${ch}`;
    }
    default:
      return steps[steps.length - 1]!.label || node?.label || wait(String(steps[steps.length - 1]!.key));
  }
}

/** Сборки (`signal`) уровней дерева в кэше — для сверки с карточкой. */
function treeSignals(qc: QueryClient): Set<string> {
  const out = new Set<string>();
  for (const [, data] of qc.getQueriesData<InfiniteData<WholesaleTree>>({ queryKey: ["wholesale", "tree"] })) {
    for (const p of data?.pages ?? []) out.add(p.signal);
  }
  return out;
}

/** Карточка сущности: заголовок, KPI отгрузок с Δ, портфель заказов, строки. `treeLevels` — уровни
 *  дерева на странице: карточка узла (`t:`) с тем же началом пути запрашивает `/node` со всей
 *  иерархией — тот же кэш и та же область портфеля, что строка дерева. */
function EntityCard({
  card,
  steps,
  treeLevels,
  range,
  cmp,
  cmpWarning,
  asOf,
  titleId,
  onOpen,
}: {
  card: Card;
  steps: NodeStep[];
  treeLevels: TreeDim[] | null;
  range: MonthRange;
  cmp: MonthRange | null;
  cmpWarning: string | null;
  asOf: string | null;
  titleId: string;
  onOpen: (c: Card) => void;
}): React.ReactElement {
  const dims = steps.map((s) => s.dim);
  const fromTree =
    card.type === "node" && treeLevels !== null && dims.every((d, i) => treeLevels[i] === d) ? treeLevels : null;
  const levels = fromTree ?? dims;
  // «Месяц» в иерархии: и просроченные — только с отгрузкой в месяцах периода (как в дереве)
  const monthTree = levels.includes("month");
  // сравнение по месяцу не считается: месяц узла сам задаёт период (как в дереве)
  const withMonth = dims.includes("month");
  const nodeCmp = withMonth ? null : cmp;
  const q = useWholesaleNode({
    month_from: range.from,
    month_to: range.to,
    levels,
    path: steps.map((s) => s.key),
    cmp: nodeCmp,
  });
  const linesQuery = cardQuery(card, range, monthTree);
  const lines = useWholesaleLines(linesQuery);
  const node = q.data?.node ?? null;
  const first = lines.data?.lines[0];
  const subject = subjectOf(card, steps, node, first, q.isFetched && lines.isFetched);
  const last = steps[steps.length - 1]!;
  const dimLabel = TREE_DIMS.find((d) => d.code === last.dim)?.label ?? last.dim;
  const qc = useQueryClient();
  // карточка посчитана по другой сборке витрины, чем показанное дерево — сказать, а не молчать
  const signals = fromTree && q.data ? treeSignals(qc) : new Set<string>();
  const outdated = q.data !== undefined && [...signals].some((x) => x !== q.data!.signal);
  return (
    <div className="space-y-4">
      <div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{dimLabel}</div>
        <h2 id={titleId} className="text-lg font-semibold" data-testid="card-subject">
          {subject}
        </h2>
        {node?.sublabel && (
          <div className="text-xs text-muted-foreground" data-testid="card-sublabel">
            {node.sublabel}
          </div>
        )}
        {last.dim === "buyer" && node && isCommissioner(node) && (
          <p className="text-xs text-muted-foreground" data-testid="card-commissioner">
            {COMMISSIONER_HINT}
          </p>
        )}
        {card.type === "node" && steps.length > 1 && (
          <p className="text-xs text-muted-foreground" data-testid="card-filters">
            Фильтр: {stepsText(steps)}
          </p>
        )}
      </div>
      {outdated && (
        <div role="status" className={AMBER} data-testid="card-outdated">
          Данные обновились (витрина пересобрана) — цифры карточки новее дерева; обновите дерево.
        </div>
      )}
      {q.isError && <ErrorBox error={q.error} what="показатели карточки" />}
      {!q.data && !q.isError && <div className="text-sm text-muted-foreground">Загружаем показатели…</div>}
      {q.data && (
        <>
          <Block title={`Отгружено · ${periodLabel(range)}${nodeCmp ? ` · сравнение — ${periodLabel(nodeCmp)}` : ""}`}>
            {withMonth && cmp && (
              <p className="text-xs text-muted-foreground" data-testid="card-no-cmp">
                Сравнение в разрезе «Месяц» не считается: месяц сам задаёт период.
              </p>
            )}
            {node === null ? (
              <p className="text-sm text-muted-foreground" data-testid="card-no-node">
                Ни отгрузок и заказов к отгрузке в периоде, ни просроченных{nodeCmp ? ", ни отгрузок в периоде сравнения" : ""} нет.
              </p>
            ) : (
              <ShippedKpis
                shipped={node.shipped}
                cmp={nodeCmp ? node.shipped_cmp : undefined}
                warning={nodeCmp ? cmpWarning : null}
                compact
              />
            )}
          </Block>
          {node &&
            (asOf ? (
              <Portfolio planned={node.planned} overdue={node.overdue} asOf={asOf} periodBound={monthTree} />
            ) : (
              <p className={AMBER} role="status" data-testid="card-no-portfolio">
                Портфель заказов не показан: нет состояния витрины (дата снимка остатка неизвестна) — сводка и /meta не
                ответили.
              </p>
            ))}
        </>
      )}
      <LinesBlock query={linesQuery} totals={false} onOpen={onOpen} />
    </div>
  );
}

export function CardPanel({
  card,
  range,
  treeLevels,
  cmp,
  cmpWarning,
  asOf,
  onClose,
  onBack,
  onOpen,
}: {
  card: Card;
  range: MonthRange;
  /** уровни дерева на странице (null — дерево не построено): карточка узла берёт их для `/node` */
  treeLevels: TreeDim[] | null;
  /** период сравнения (Δ KPI сущности); null — без сравнения */
  cmp: MonthRange | null;
  /** Δ ненадёжна (сравнение вне данных) — ⚠ у Δ */
  cmpWarning: string | null;
  /** дата снимка остатка заказов (портфель); нет состояния витрины — null */
  asOf: string | null;
  onClose: () => void;
  /** «Назад» к предыдущей карточке (открыли из карточки) */
  onBack?: () => void;
  /** открыть другую карточку из этой (новая запись истории) */
  onOpen: (c: Card) => void;
}): React.ReactElement {
  const steps = entitySteps(card);
  return (
    <EntityPanelFrame
      label={cardLabel(card)}
      cardId={encodeCard(card)}
      onClose={onClose}
      onBack={onBack}
      widthClass="w-[min(1000px,100vw)]"
    >
      {(titleId) =>
        steps ? (
          <EntityCard
            card={card}
            steps={steps}
            treeLevels={treeLevels}
            range={range}
            cmp={cmp}
            cmpWarning={cmpWarning}
            asOf={asOf}
            titleId={titleId}
            onOpen={onOpen}
          />
        ) : (
          <div className="space-y-3">
            <h2 id={titleId} className="text-lg font-semibold" data-testid="card-subject">
              {listTitle(card, range)}
            </h2>
            {card.type === "unpriced" && <ProblemProducts range={range} onOpen={onOpen} kind="unpriced" />}
            {card.type === "norate" && <ProblemProducts range={range} onOpen={onOpen} kind="no_rate" />}
            <LinesBlock query={cardQuery(card, range)} totals onOpen={onOpen} />
          </div>
        )
      }
    </EntityPanelFrame>
  );
}
